import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import DataSource from 'devextreme/data/data_source';
import ODataStore from 'devextreme/data/odata/store';
import { RtTripShopForm } from '@bds/railtrac-models';

export interface ShopFormCreateRequest {
    carInit: string;
    carNo: string;
    shipDateTime: Date;
    custNo?: string;
}

export interface TripShopFormRequest {
    carInit: string;
    carNo: string;
    shipDateTime: Date;
    custNo?: string;
}

@Injectable({
    providedIn: 'root',
})
export class RtShopFormService {
    private readonly baseUrl: string;
    private readonly odataUrl: string;

    constructor(
        private http: HttpClient,
        private fb: UntypedFormBuilder,
        @Inject('BASE_API_URL') private apibaseUrl: string,
        @Inject('BASE_ODATA_URL') private odatabaseUrl: string,
    ) {
        this.baseUrl = this.apibaseUrl;
        this.odataUrl = this.odatabaseUrl;
    }

    /**
     * Create a single form group for shop form
     */
    createShopFormGroup(shopForm?: RtTripShopForm): UntypedFormGroup {
        const formGroup = this.fb.group({
            // Basic Information
            carInit: [shopForm?.carInit || '', [Validators.required, Validators.maxLength(4)]],
            carNo: [shopForm?.carNo || '', [Validators.required, Validators.maxLength(6)]],
            shipDateTime: [shopForm?.shipDateTime || '', Validators.required],
            fleetId: [shopForm?.fleetId || ''],
            businessGroup: [shopForm?.businessGroup || ''],
            cleanSafeEntry: [shopForm?.cleanSafeEntry || ''],
            locCity: [shopForm?.locCity || ''],
            locState: [shopForm?.locState || ''],
            lastCommCode: [shopForm?.lastCommCode || ''],

            // Test Information - Tank
            prevTankTest: [shopForm?.prevTankTest || ''],
            tankTestDue: [shopForm?.tankTestDue || ''],

            // Test Information - Valve
            prevValveTest: [shopForm?.prevValveTest || ''],
            valveTestDue: [shopForm?.valveTestDue || ''],
            valveType: [shopForm?.valveType || ''],

            // Test Information - Coil
            prevCoilTest: [shopForm?.prevCoilTest || ''],
            coilTestDue: [shopForm?.coilTestDue || ''],
            coilType: [shopForm?.coilType || ''],

            // Test Information - Spark
            prevSparkTest: [shopForm?.prevSparkTest || ''],
            sparkTestDue: [shopForm?.sparkTestDue || ''],

            // Test Information - Stub Sill
            prevStubSillTest: [shopForm?.prevStubSillTest || ''],
            stubSillTestDue: [shopForm?.stubSillTestDue || ''],

            // Paint Information
            prevPaintDate: [shopForm?.prevPaintDate || ''],
            paintDue: [shopForm?.paintDue || ''],
            paintType: [shopForm?.paintType || ''],
            paintCondition: [shopForm?.paintCondition || ''],

            // Lining Information
            prevLiningDate: [shopForm?.prevLiningDate || ''],
            liningDue: [shopForm?.liningDue || ''],
            liningType: [shopForm?.liningType || ''],
            liningCondition: [shopForm?.liningCondition || ''],
            prevLiningTest: [shopForm?.prevLiningTest || ''],
            liningTestDue: [shopForm?.liningTestDue || ''],

            // Inspection Information
            inspect88b2Prev: [shopForm?.inspect88b2Prev || ''],
            inspect88b2Due: [shopForm?.inspect88b2Due || ''],
            shellThicknessPrev: [shopForm?.shellThicknessPrev || ''],
            shellThicknessDue: [shopForm?.shellThicknessDue || ''],

            // Maintenance Information
            lastMaintDate: [shopForm?.lastMaintDate || ''],
            nextMaintDate: [shopForm?.nextMaintDate || ''],

            // Dynamic Date Fields (1-10)
            date1FieldName: [shopForm?.date1FieldName || ''],
            date1Prev: [shopForm?.date1Prev || ''],
            date1Due: [shopForm?.date1Due || ''],
            date2FieldName: [shopForm?.date2FieldName || ''],
            date2Prev: [shopForm?.date2Prev || ''],
            date2Due: [shopForm?.date2Due || ''],
            date3FieldName: [shopForm?.date3FieldName || ''],
            date3Prev: [shopForm?.date3Prev || ''],
            date3Due: [shopForm?.date3Due || ''],
            date4FieldName: [shopForm?.date4FieldName || ''],
            date4Prev: [shopForm?.date4Prev || ''],
            date4Due: [shopForm?.date4Due || ''],
            date5FieldName: [shopForm?.date5FieldName || ''],
            date5Prev: [shopForm?.date5Prev || ''],
            date5Due: [shopForm?.date5Due || ''],
            date6FieldName: [shopForm?.date6FieldName || ''],
            date6Prev: [shopForm?.date6Prev || ''],
            date6Due: [shopForm?.date6Due || ''],
            date7FieldName: [shopForm?.date7FieldName || ''],
            date7Prev: [shopForm?.date7Prev || ''],
            date7Due: [shopForm?.date7Due || ''],
            date8FieldName: [shopForm?.date8FieldName || ''],
            date8Prev: [shopForm?.date8Prev || ''],
            date8Due: [shopForm?.date8Due || ''],
            date9FieldName: [shopForm?.date9FieldName || ''],
            date9Prev: [shopForm?.date9Prev || ''],
            date9Due: [shopForm?.date9Due || ''],
            date10FieldName: [shopForm?.date10FieldName || ''],
            date10Prev: [shopForm?.date10Prev || ''],
            date10Due: [shopForm?.date10Due || ''],

            // Route and Customer Information
            routeCode: [shopForm?.routeCode || '', Validators.maxLength(20)],
            routeDscr: [{ value: shopForm?.routeDscr || '', disabled: true }],
            custNo: [shopForm?.custNo || '', Validators.maxLength(13)],
            custFname: [{ value: shopForm?.custFname || '', disabled: true }],
            custLname: [{ value: shopForm?.custLname || '', disabled: true }],

            // Comments
            shopComments: [shopForm?.shopComments || '', Validators.maxLength(1000)],
            specialRequirements: [
                { value: shopForm?.specialRequirements || '', disabled: false },
                Validators.maxLength(1000),
            ],
            additionalComments: [
                { value: shopForm?.additionalComments || '', disabled: false },
                Validators.maxLength(1000),
            ],

            // System Information
            userId: [{ value: shopForm?.userId || '', disabled: true }],
            updateDate: [{ value: shopForm?.updateDate || new Date(), disabled: true }],
            ormId: [shopForm?.ormId || ''],
            rtTripOrmId: [shopForm?.rtTripOrmId || ''],
            updateDateTime: [{ value: shopForm?.updateDateTime || new Date(), disabled: true }],
            stcc: [''],
            productName: [''],
            carCapacity: [''],
            lessorRiderNo: [''],
            lessorBeginDate: [''],
            lessorEndDate: [''],
            shopName: [''],
            shopLocation: [''],
            shopContact: [''],
            faxNumber: [''],
            email: [''],
        });

        return formGroup;
    }

    /**
     * Convert form group to RtTripShopForm object
     */
    convertFormToShopForm(formGroup: UntypedFormGroup): RtTripShopForm {
        const formValue = formGroup.getRawValue();
        const shopForm = new RtTripShopForm();

        // Map all form values to the shop form object
        Object.keys(formValue).forEach((key) => {
            if (formValue[key] !== null && formValue[key] !== undefined) {
                shopForm[key] = formValue[key];
            }
        });

        return shopForm;
    }

    /**
     * Create shop form by calling stored procedure
     */
    createShopForm(request: ShopFormCreateRequest): Observable<RtTripShopForm> {
        const url = `${this.baseUrl}TripShopForm/create`;
        return this.http.post<RtTripShopForm>(url, request);
    }

    /**
     * Generate shop form by calling the stored procedure
     * Maps to [HttpPost("generate")] endpoint
     */
    generateShopForm(request: TripShopFormRequest): Observable<RtTripShopForm> {
        const url = `${this.baseUrl}TripShopForm/generate`;
        return this.http.post<RtTripShopForm>(url, request);
    }

    /**
     * Get shop form by calling the stored procedure
     * Maps to [HttpGet("trip/{tripOrmId}")] endpoint
     */
    getShopForm(tripOrmId: number): Observable<RtTripShopForm> {
        const url = `${this.baseUrl}TripShopForm/trip/${tripOrmId}`;
        return this.http.get<RtTripShopForm>(url);
    }

    /**
     * Update shop form
     */
    updateShopForm(shopForm: RtTripShopForm): Observable<RtTripShopForm> {
        const url = `${this.baseUrl}TripShopForm/${shopForm?.ormId}`;
        return this.http.put<RtTripShopForm>(url, shopForm);
    }
}
